import React, { useState } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { loadStripe } from '@stripe/stripe-js';
import './Credits.css';

const stripePromise = loadStripe('pk_live_51PySXtBq6BngvjtVWinZJVVoy78lHcus1ZLsoNfFIGWujSYDGpUPuhfcqpw8EBfSagHTODAfoznkQMEpTsouv90F00t5WRoTKD');

function Credits() {
  const { user } = useAuth();
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [activeFaq, setActiveFaq] = useState(null);
  const [paymentType, setPaymentType] = useState('oneTime'); // 'oneTime' or 'monthly'

  const toggleFaq = (index) => {
    if (activeFaq === index) {
      setActiveFaq(null);
    } else {
      setActiveFaq(index);
    }
  };

  const creditPlans = [
    { 
      packageId: "starter",
      name: "Starter Pack",
      credits: 150, 
      oneTimePrice: 50, 
      monthlyPrice: 40,
      bestFor: "Small businesses starting out with address management",
      features: [
        "No expiration date",
        "Use for any HubSpot portal",
        "HubSpot integration"
      ]
    },
    { 
      packageId: "growth",
      name: "Growth Pack",
      credits: 600, 
      oneTimePrice: 150, 
      monthlyPrice: 120,
      bestFor: "Growing businesses with regular contact management",
      features: [
        "No expiration date",
        "Use for any HubSpot portal",
        "HubSpot integration"
      ]
    },
    { 
      packageId: "scale",
      name: "Scale Pack",
      credits: 2000, 
      oneTimePrice: 300, 
      monthlyPrice: 240,
      bestFor: "Established businesses with high volume needs",
      features: [
        "No expiration date",
        "Use for any HubSpot portal",
        "HubSpot integration"
      ],
      popular: true
    },
    { 
      packageId: "enterprise",
      name: "Enterprise",
      credits: 5000, 
      oneTimePrice: 500, 
      monthlyPrice: 400,
      bestFor: "Large organizations with specific requirements",
      features: [
        "No expiration date",
        "Use for any HubSpot portal",
        "HubSpot integration"
      ]
    },
  ];

  const faqs = [
    {
      question: "What types of postal codes are supported?",
      answer: "ZipCode AutoFill supports both 5-digit US ZIP codes and, to a limited degree, Canadian postal codes (e.g., K1L 6P3). Canadian postal codes are best supported in situations where you only use Canadian zip codes. For Canadian zip codes, state correponds to the 2 letter province abbreviation, and county corresponds to the UTC timezone. We do not support ZIP+4 codes or other international formats. Each lookup of a valid postal code consumes one credit."
    },
    {
      question: "Do credits expire?",
      answer: "No, your credits never expire. Use them at your own pace without worrying about timelines."
    },
    {
      question: "When are credits consumed?",
      answer: "Credits are only consumed for successful lookups. If a ZIP code cannot be found or is invalid, no credit is deducted from your account."
    },
    {
      question: "How do monthly subscriptions work?",
      answer: "Monthly subscriptions automatically add credits to your account each month. You can cancel anytime through the Stripe customer portal. Credits from subscriptions never expire, just like one-time purchases."
    }
  ];

  const handlePaymentTypeChange = (type) => {
    console.log('Changing payment type to:', type); // Debug log
    setPaymentType(type);
  };

  const handlePurchase = async (packageId) => {
    console.log('Purchase initiated for:', packageId, 'Payment type:', paymentType); // Debug log
    setError(''); // Clear any previous errors
    try {
      const stripe = await stripePromise;
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/auth/create-checkout-session`, {
        packageId,
        paymentType
      }, {
        headers: {
          'Authorization': `Bearer ${user.token}`,
          'Content-Type': 'application/json'
        }
      });
      
      const result = await stripe.redirectToCheckout({
        sessionId: response.data.sessionId,
      });

      if (result.error) {
        setError(result.error.message);
      }
    } catch (error) {
      console.error('Purchase error:', error);
      if (error.response && error.response.data && error.response.data.error) {
        setError(error.response.data.error);
      } else {
        setError('Failed to initiate purchase');
      }
    }
  };

  return (
    <div className="credits-container">
      <div className="credits-header">
        <div className="credits-shapes">
          <div className="credits-shape shape1"></div>
          <div className="credits-shape shape2"></div>
          <div className="credits-shape shape3"></div>
        </div>
        <h1>Purchase Credits</h1>
        <p className="credits-intro">
          Choose a credit package that suits your needs. The more credits you buy, the more you save!
        </p>
        {message && <p className="success">{message}</p>}
        {error && <p className="error">{error}</p>}
        
        {/* Payment Type Toggle */}
        <div className="payment-type-toggle">
          <button 
            className={`toggle-btn ${paymentType === 'oneTime' ? 'active' : ''}`}
            onClick={() => handlePaymentTypeChange('oneTime')}
          >
            One-Time Purchase
          </button>
          <button 
            className={`toggle-btn ${paymentType === 'monthly' ? 'active' : ''}`}
            onClick={() => handlePaymentTypeChange('monthly')}
          >
            Monthly Subscription
          </button>
        </div>
      </div>

      <div className="credit-plans">
        {creditPlans.map((plan, index) => {
          const currentPrice = paymentType === 'monthly' ? plan.monthlyPrice : plan.oneTimePrice;
          const period = paymentType === 'monthly' ? 'per month' : 'one-time payment';
          const perCredit = paymentType === 'monthly' 
            ? `$${(plan.monthlyPrice / plan.credits).toFixed(3)} per credit/month`
            : `$${(plan.oneTimePrice / plan.credits).toFixed(2)} per credit`;
          
          // Calculate savings for monthly vs one-time per credit
          const oneTimeCostPerCredit = plan.oneTimePrice / plan.credits;
          const monthlyCostPerCredit = plan.monthlyPrice / plan.credits;
          const savingsPercent = Math.round(((oneTimeCostPerCredit - monthlyCostPerCredit) / oneTimeCostPerCredit) * 100);
          
          return (
            <div key={index} className={`credit-plan ${plan.popular ? 'popular' : ''}`}>
              {plan.popular && <div className="popular-badge">Most Popular</div>}
              <h2 className="plan-name">{plan.name}</h2>
              <div className="plan-price">
                <span className="price">${currentPrice}</span>
                <span className="period">{period}</span>
                {paymentType === 'monthly' && savingsPercent > 0 && (
                  <div className="savings-badge">
                    Save {savingsPercent}% vs one-time
                  </div>
                )}
              </div>
              <div className="plan-credits">
                <span className="credits-amount">{plan.credits.toLocaleString()}</span>
                <span className="credits-label">Credits {paymentType === 'monthly' ? 'per month' : ''}</span>
                <span className="per-credit">{perCredit}</span>
              </div>
              <p className="plan-description">{plan.bestFor}</p>
              <ul className="plan-features">
                {plan.features.map((feature, i) => (
                  <li key={i}>
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12"></polyline>
                    </svg>
                    {feature}
                  </li>
                ))}
              </ul>
              <div className="plan-action">
                <button onClick={() => handlePurchase(plan.packageId)} className="plan-button">
                  {paymentType === 'monthly' ? 'Subscribe' : 'Purchase'}
                </button>
              </div>
            </div>
          );
        })}
      </div>

      <div className="custom-credits-box">
        <div className="custom-credits-content">
          <div className="custom-credits-icon">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="12" y1="8" x2="12" y2="16"></line>
              <line x1="8" y1="12" x2="16" y2="12"></line>
            </svg>
          </div>
          <div className="custom-credits-text">
            <h2>Need more credits or a custom package?</h2>
            <p>We offer flexible solutions for businesses with unique requirements. Contact us to discuss volume discounts, enterprise licensing, or custom integrations tailored to your specific needs.</p>
          </div>
          <div className="custom-credits-action">
            <a href="mailto:info@zipcodeautofill.com" className="custom-credits-button">
              Contact Us
            </a>
          </div>
        </div>
      </div>

      <div className="pricing-faq">
        <h2>Frequently Asked Questions</h2>
        <div className="faq-list">
          {faqs.map((faq, index) => (
            <div 
              key={index} 
              className={`faq-item ${activeFaq === index ? 'active' : ''}`}
              onClick={() => toggleFaq(index)}
            >
              <div className="faq-question">
                <h3>{faq.question}</h3>
                <div className="faq-icon">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="12" y1="5" x2="12" y2="19"></line>
                    <line x1="5" y1="12" x2="19" y2="12" className="vertical-line"></line>
                  </svg>
                </div>
              </div>
              <div className="faq-answer">
                <p>{faq.answer}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="pricing-guarantee">
        <div className="guarantee-icon">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
          </svg>
        </div>
        <div className="guarantee-content">
          <h3>Our Guarantee</h3>
          <p>
            Credits are only consumed for successful lookups. If a ZIP code cannot be found or is invalid, 
            no credit is deducted from your account. If you ever have a credit dispute, please reach out to our Support team and we will take care of you.
          </p>
        </div>
      </div>
    </div>
  );
}

export default Credits;
